import { ArrowLeftIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { BarChart } from "../../components/ui/bar-chart";
import { Button } from "../../components/ui/button";
import { Card, ErrorState, Loading, PageHeader, StatCard, Toolbar } from "../../components/ui/misc";
import { formatDuration, formatNumber } from "../../lib/format";
import { gameAnalyticsQuery } from "../../lib/queries";
import { RangePicker, resolveRange } from "./range";

export function GameAnalyticsPage() {
  const { gameId } = useParams({ from: "/authed/analytics/games/$gameId" });
  const search = useSearch({ from: "/authed/analytics/games/$gameId" });
  const navigate = useNavigate();
  const range = resolveRange(search);
  const analytics = useQuery(gameAnalyticsQuery(Number(gameId), range));

  if (analytics.error)
    return <ErrorState error={analytics.error} onRetry={() => void analytics.refetch()} />;
  if (analytics.isPending) return <Loading />;

  const breakdown = (rows: { label: string; value: number }[]) => {
    const total = rows.reduce((sum, row) => sum + row.value, 0) || 1;
    return (
      <ul className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <li key={row.label} className="text-caption flex items-center gap-2">
            <span className="text-body w-16 shrink-0 uppercase">{row.label}</span>
            <span className="bg-raised h-1.5 flex-1 rounded">
              <span
                className="bg-accent/70 block h-full rounded"
                style={{ width: `${(row.value / total) * 100}%` }}
              />
            </span>
            <span className="tabular text-muted w-14 text-right">{formatNumber(row.value)}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <PageHeader
        title={analytics.data.game.name}
        description={`/${analytics.data.game.slug}`}
        actions={
          <>
            <Link to="/analytics" search={range}>
              <Button variant="ghost">
                <ArrowLeftIcon /> Analytics
              </Button>
            </Link>
            <Link to="/games/$gameId" params={{ gameId }}>
              <Button>
                <PencilSimpleIcon /> Edit game
              </Button>
            </Link>
          </>
        }
      />

      <Toolbar>
        <RangePicker
          range={range}
          onChange={(next) =>
            void navigate({ to: "/analytics/games/$gameId", params: { gameId }, search: next })
          }
        />
        <span className="text-muted text-caption">
          {range.from} → {range.to}
        </span>
      </Toolbar>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Views" value={formatNumber(analytics.data.totals.views)} />
        <StatCard label="Launches" value={formatNumber(analytics.data.totals.launches)} />
        <StatCard label="Play time" value={formatDuration(analytics.data.totals.playSeconds)} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Launches per day">
          <BarChart
            points={analytics.data.daily.map((point) => ({
              label: point.day,
              value: point.launches,
            }))}
          />
        </Card>
        <Card title="Play time per day">
          <BarChart
            points={analytics.data.daily.map((point) => ({
              label: point.day,
              value: point.playSeconds,
            }))}
          />
        </Card>
        <Card title="Countries">
          {breakdown(
            analytics.data.byCountry.map((row) => ({ label: row.country, value: row.launches })),
          )}
        </Card>
        <Card title="Devices">
          {breakdown(
            analytics.data.byDevice.map((row) => ({ label: row.device, value: row.launches })),
          )}
        </Card>
      </div>
    </>
  );
}
